import { initialize, LDClient, LDFlagSet } from 'launchdarkly-js-client-sdk';
import { createContext, ReactNode, useContext, useEffect, useMemo, useState } from 'react';
import { SubscriptionPlanPrice } from '../models/subscriptionPlanPrice';
import { DirectEcomTransition } from '../models/directEcomTransition';
import { useQuery } from '../routes';
import { Feature } from '../models/feature';
import { EcomEligibility, getEcomEligibility } from '../services/subscription';
import { overridePlanPriceForEcom } from './ecomPlanPrice';
import { hasAccountUuidHeader } from '../services/axios';
import { reportFrontendLog } from '../../frontendObservability';

interface LaunchDarklyProps {
  client?: LDClient;
  initialized?: boolean;
  flags?: LaunchDarklyFlag;
  currentOrganizationId?: number;
  currentAccountId?: number;
  changeAccount?: (accountId: number) => Promise<void>;
  // Per-feature eCom channel eligibility of the current account (KSE product#1447, extended to
  // KRE / TestCloud / TestOps G3 by New Packaging Sep 2026). Consumers need this because ecom
  // pricing/promotion is conditional per account for these features, unlike TA/TP. Empty until
  // the BE has answered (or on any failure).
  ecomEligibility?: EcomEligibility;
  // Backward-compatible shorthand: `ecomEligibility[Feature.PER_USER_KSE] ?? false`.
  kseEcomEligible?: boolean;
}

interface LaunchDarklyFlag extends Omit<LDFlagSet, keyof LDFlagSet> {
  subscriptionAndPaymentMethodDisabled?: boolean;
  unlimitedVTPEnabled?: boolean;
  bankTransferEnabled?: boolean;
  monthlySubsYearEndCampaignConfig?: any;
  truetestUIEnabled?: boolean;
  subscriptionPlanPrice: SubscriptionPlanPrice;
  directEcomTransition?: DirectEcomTransition;
  subscriptionRetryPayment?: boolean;
  paidDomainsPromoExclusionConfig?: string[];
  navigationBarEnabled?: boolean;
  sidebarEnabled?: boolean;
  showPLanBanner?: boolean;
  showSaveCardAndBillingInfoBtn?: boolean;
  orderHistoryActionBtnEnabled?: boolean;
  cancelCreditCardOrderEnabled?: boolean;
  turnOffAutoRenewSurveyEnabled?: boolean;
  cookieExpirationDays?: number;
  truePlatformEnabled?: boolean;
  // Direct-sale (sales-managed) accounts render every subscription as contract-managed
  // on the subscription page (locked auto-renew + contract renewal note).
  // Supports both flag shapes: a boolean flag with per-account targeting rules
  // (LD serves true for listed accounts), or a JSON array of account IDs.
  directSaleAccountIds?: boolean | Array<number | string>;
}

const defaultValue: LaunchDarklyFlag = {
  subscriptionAndPaymentMethodDisabled: false,
  unlimitedVTPEnabled: false,
  bankTransferEnabled: false,
  monthlySubsYearEndCampaignConfig: undefined,
  truetestUIEnabled: false,
  subscriptionPlanPrice: {},
  directEcomTransition: undefined,
  subscriptionRetryPayment: false,
  paidDomainsPromoExclusionConfig: [],
  navigationBarEnabled: false,
  sidebarEnabled: true,
  showPLanBanner: false,
  showSaveCardAndBillingInfoBtn: true,
  orderHistoryActionBtnEnabled: false,
  cancelCreditCardOrderEnabled: false,
  turnOffAutoRenewSurveyEnabled: false,
  cookieExpirationDays: 30,
  truePlatformEnabled: false,
  directSaleAccountIds: false,
};

// Dev-only merge into subscriptionPlanPrice, e.g. when a price key is not yet seeded on
// the LD env: REACT_APP_SUBSCRIPTION_PLAN_PRICE_OVERRIDES={"trueAutomationDiscountAnnually":1699}
const parseLocalPlanPriceOverrides = (): SubscriptionPlanPrice | undefined => {
  const raw = process.env.REACT_APP_SUBSCRIPTION_PLAN_PRICE_OVERRIDES;
  if (!raw) {
    return undefined;
  }
  try {
    return JSON.parse(raw) as SubscriptionPlanPrice;
  } catch {
    reportFrontendLog('warn', 'launchdarkly.local_price_override.invalid', {
      bundle: 'master-app-ui',
      component: 'LaunchDarklyProvider',
    });
    return undefined;
  }
};

const applyLocalFlagOverrides = (flags: LaunchDarklyFlag): LaunchDarklyFlag => {
  if (process.env.NODE_ENV !== 'development') {
    return flags;
  }

  const planPriceOverrides = parseLocalPlanPriceOverrides();

  return {
    ...flags,
    ...(planPriceOverrides
      ? { subscriptionPlanPrice: { ...flags.subscriptionPlanPrice, ...planPriceOverrides } }
      : {}),
  };
};

const LaunchDarklyContext = createContext<LaunchDarklyProps>({});

export const LaunchDarklyProvider = ({ children }: { children: ReactNode }) => {
  const [flags, setFlags] = useState<LaunchDarklyProps['flags']>(
    applyLocalFlagOverrides(defaultValue),
  );
  const [client, setClient] = useState<LaunchDarklyProps['client']>();
  const [initialized, setInitialized] = useState<LaunchDarklyProps['initialized']>(false);
  const { get } = useQuery();
  const accountId = get('accountId');
  const [currentAccountId, setCurrentAccountId] = useState<LaunchDarklyProps['currentAccountId']>();
  const [ecomEligibility, setEcomEligibility] = useState<EcomEligibility>({});
  const initializeClient = async () => {
    if (!process.env.REACT_APP_LD_KEY || !process.env.REACT_APP_LD_USER) {
      setInitialized(true);
      return Promise.resolve('Missing LaunchDarkly Configuration');
    }
    const ldUser = {
      key: process.env.REACT_APP_LD_USER,
    };
    const ldClient = initialize(process.env.REACT_APP_LD_KEY!!, ldUser);
    setClient(ldClient);
    return ldClient.waitForInitialization()
      .then(() => {
        setInitialized(true);
      });
  };

  const changeAccount = async (accountId: number) => {
    if (!initialized || !client || currentAccountId === accountId) {
      return;
    }
    setCurrentAccountId(accountId);
    const key = `account-${process.env.REACT_APP_LD_PREFIX}-${accountId}`;
    const accountFlags = await client.identify({
      kind: 'user',
      key,
      accountId,
    }) as LaunchDarklyFlag;
    setFlags(applyLocalFlagOverrides(accountFlags));
  };

  const getAllFlags = () => {
    if (initialized && client) {
      const data = client.allFlags();
      setFlags(applyLocalFlagOverrides(data as LaunchDarklyFlag));
      return data;
    }
    return {};
  };

  useEffect(() => {
    if (Number.isNaN(accountId)) {
      getAllFlags();
      return;
    }
    changeAccount(Number(accountId))
      .then(() => {
        // do nothing
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialized]);

  useEffect(() => {
    if (!currentAccountId) {
      setEcomEligibility({});
      return undefined;
    }
    let cancelled = false;
    let retries = 0;
    const fetchEligibility = () => {
      if (cancelled) {
        return;
      }
      // changeAccount fires from the URL query at LD init, BEFORE account verification has run
      // doSetSelectedAccount (which sets the X-Account-Id header). Calling CE without the header
      // 400s (account.not.provided) into the global error toast — wait for the header instead.
      if (!hasAccountUuidHeader()) {
        retries += 1;
        if (retries <= 20) {
          setTimeout(fetchEligibility, 500);
        }
        return;
      }
      getEcomEligibility()
        .then(eligibility => {
          if (!cancelled) {
            setEcomEligibility(eligibility);
          }
        })
        .catch(() => {
          // Not eligible on any failure — legacy listed price is the safe display fallback.
          if (!cancelled) {
            setEcomEligibility({});
          }
        });
    };
    fetchEligibility();
    return () => {
      cancelled = true;
    };
  }, [currentAccountId]);

  useEffect(() => {
    initializeClient();
    return function cleanup() {
      if (client) {
        client.close()
          .then(() => {
            // do nothing
          });
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const effectiveFlags = useMemo(
    () => overridePlanPriceForEcom(flags, ecomEligibility),
    [flags, ecomEligibility],
  );
  const kseEcomEligible = ecomEligibility[Feature.PER_USER_KSE] ?? false;

  return (
    <LaunchDarklyContext.Provider
      value={{
        client,
        flags: effectiveFlags,
        initialized,
        currentAccountId,
        changeAccount,
        ecomEligibility,
        kseEcomEligible,
      }}
    >
      {children}
    </LaunchDarklyContext.Provider>
  );
};

export const useLaunchDarkly = () => useContext(LaunchDarklyContext);
